import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor, Smartphone } from 'lucide-react';
import { cn } from 'lib/utils';
import { QuestionPreview, type QuestionPreviewData } from 'components/questions';
import type { QuestionType } from '@examing/types';

/**
 * Carries the type-specific authoring fields too, so the preview can render the
 * eight non-choice types instead of just their stem.
 */
export interface PreviewDraft extends Partial<QuestionPreviewData> {
  type: QuestionType;
  points: number;
  prompt: string;
  options: { id: string; value: string }[];
  correctAnswer: string | string[] | Record<string, string>;
  typeLabel: string;
  questionNumber?: number;
}

/** Renders through the shared `QuestionPreview`, so math/images/type layout match the candidate view. */
export const LivePlayerPreview: React.FC<{ draft: PreviewDraft }> = ({ draft }) => {
  const { t } = useTranslation();
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');

  return (
    <div className="flex h-full flex-col bg-[#F9FAFB] dark:bg-muted/30">
      {/* Preview toolbar */}
      <div className="flex items-center justify-between px-6 py-4">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('editor_live_preview')} <span className="font-normal normal-case">— {t('editor_preview_hint')}</span>
        </span>
        <div className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-card p-0.5">
          {([
            { id: 'desktop', labelKey: 'editor_preview_desktop', Icon: Monitor },
            { id: 'mobile', labelKey: 'editor_preview_mobile', Icon: Smartphone },
          ] as const).map(({ id, labelKey, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setDevice(id)}
              aria-label={t(labelKey)}
              title={t(labelKey)}
              className={cn(
                'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors',
                device === id ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
            </button>
          ))}
        </div>
      </div>

      {/* Preview canvas */}
      <div className="flex flex-1 justify-center overflow-y-auto px-6 pb-8">
        <div className={cn('w-full transition-all', device === 'mobile' ? 'max-w-[380px]' : 'max-w-[560px]')}>
          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-sm">
              <span className="rounded-md bg-muted px-2.5 py-0.5 text-xs font-semibold text-foreground">
                {t('question')} {draft.questionNumber ?? 1}
              </span>
              <span className="text-muted-foreground">
                {draft.typeLabel} · {t('n_points', { count: draft.points })}
              </span>
            </div>

            <QuestionPreview question={draft as QuestionPreviewData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivePlayerPreview;
